import {useActionSheet} from "@expo/react-native-action-sheet"
import {addToSortedArray, assert, runAsync, unreachable} from "@prosay/common"
import {
    AudioPlayer,
    ChatBubble,
    InvertedFlatList,
    KeyboardSpacer,
    MessageStatus,
    useSnackbarMessage,
} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {
    Clipboard,
    Dimensions,
    Image,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import {
    ActivityIndicator,
    Appbar,
    Banner,
    Button,
    TouchableRipple,
} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {
    ConversationImageContextProvider,
    MessageEditInputBar,
    MessagesInputBar,
    useConversationImage,
} from "../../components"
import {HookOptimisticResponseSync} from "../../data/types"
import {
    ConversationInfoFragment,
    GetConversationQuery,
    GetMessagesDocument,
    GetMessagesQuery,
    GetMessagesQueryVariables,
    LastMessageFragment,
    LastMessageFragmentDoc,
    MessageInfoFragment,
    useCurrentUserQuery,
    useGetConversationQuery,
    useMarkConversationAsLookingForLawyerMutation,
    useRemoveMessageMutation,
    useResolveCaseMutation,
    useUndoRemoveMessageMutation,
    useUnresolveCaseMutation,
} from "../../graphql"
import {useDate, useMessages} from "../../hooks"
import {useConversationUpdateSubscription} from "../../hooks/conversationUpdate"
import {
    compareMessagesByDate,
    getStatusText,
    isOptimisticId,
    readQuery,
} from "../../util"

// TODO: Increase the number of items loaded for each page

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    appbarHeaderStyle: {
        marginBottom: 10,
    },

    container: {
        flex: 1,
    },

    lookingForLawyerContainer: {},
    messageList: {
        flex: 1,
    },

    messageListItemContainer: {
        flex: 1,
    },

    messagesHeaderSubtitleStyle: {
        flexWrap: "wrap",
    },
})

export interface AudioMessageListItemProps {
    audioContentUrl: string
    date: Date
    highlight?: boolean
    id: string
    isMe: boolean
    onLongPress?: () => void
    onPress?: () => void
    status: MessageStatus | undefined
}
export const AudioMessageListItem: React.FC<AudioMessageListItemProps> = ({
    audioContentUrl,
    date,
    highlight,
    id,
    isMe,
    onLongPress,
    onPress,
    status,
}) => (
    <View style={styles.messageListItemContainer}>
        <ChatBubble
            key={id}
            {...{date, highlight, isMe, onLongPress, onPress, status}}>
            <AudioPlayer
                audio={{uri: audioContentUrl}}
                onLongPress={onLongPress}
            />
        </ChatBubble>
    </View>
)

const maintainAspectRatio = (height: number, width: number, maxWidth: number) =>
    width <= maxWidth
        ? {height, width}
        : {height: maxWidth * (height / width), width: maxWidth}

export const useImageSize = (uri: string, id: string) => {
    const [dimensions, setDimensions] = useState({height: 250, width: 250})
    useEffect(() => {
        const maxWidth = Dimensions.get("screen").width - 125
        Image.getSize(
            uri,
            (width, height) =>
                setDimensions(maintainAspectRatio(height, width, maxWidth)),
            error =>
                logger.error`Got the following error while trying to get image dimensions for message ${id}: ${error}`,
        )
    }, [id, uri])
    return dimensions
}

export interface ImageMessageListItemProps {
    date: Date
    highlight?: boolean
    id: string
    imageContentUrl: string
    isMe: boolean
    onLongPress?: () => void
    onPress?: () => void
    status: MessageStatus | undefined
}
export const ImageMessageListItem: React.FC<ImageMessageListItemProps> = ({
    date,
    highlight,
    id,
    imageContentUrl,
    isMe,
    onLongPress,
    onPress: onPress_,
    status,
}) => {
    const dimensions = useImageSize(imageContentUrl, id)
    const {show} = useConversationImage()

    const onPress = useCallback(() => {
        show(id)
        onPress_?.()
    }, [id, onPress_, show])

    return (
        <View style={styles.messageListItemContainer}>
            <ChatBubble
                key={id}
                {...{date, highlight, isMe, onLongPress, onPress, status}}>
                {imageContentUrl === "loading" ? (
                    <ActivityIndicator />
                ) : (
                    <TouchableRipple {...{onLongPress, onPress}}>
                        <Image
                            style={{
                                height: dimensions.height,
                                width: dimensions.width,
                            }}
                            source={{uri: imageContentUrl}}
                        />
                    </TouchableRipple>
                )}
            </ChatBubble>
        </View>
    )
}

export interface TextMessageListItemProps {
    content: string
    date: Date
    highlight?: boolean
    id: string
    isMe: boolean
    onLongPress?: () => void
    onPress?: () => void
    status: MessageStatus | undefined
}
export const TextMessageListItem: React.FC<TextMessageListItemProps> = ({
    content,
    date,
    highlight,
    id,
    isMe,
    onLongPress,
    onPress,
    status,
}) => (
    <View style={styles.messageListItemContainer}>
        <ChatBubble
            key={id}
            {...{date, highlight, isMe, onLongPress, onPress, status}}>
            {content}
        </ChatBubble>
    </View>
)

export const useMessageActionSheet = (
    onDelete: () => void,
    onEdit: () => void,
    onCopy: () => void,
    myMessage: boolean,
) => {
    const {showActionSheetWithOptions} = useActionSheet()
    const options = [
        ...(myMessage
            ? [
                  translate(
                      "ui.screen.messages.messageActionSheet.options.delete",
                  ),
                  translate(
                      "ui.screen.messages.messageActionSheet.options.edit",
                  ),
              ]
            : []),
        translate("ui.screen.messages.messageActionSheet.options.copy"),
        translate("ui.screen.messages.messageActionSheet.options.cancel"),
    ]
    const showMenu = useCallback(
        logger.debug(
            () =>
                showActionSheetWithOptions(
                    {
                        cancelButtonIndex: options.length - 1,
                        destructiveButtonIndex: myMessage ? 0 : undefined,
                        options,
                        title: translate(
                            "ui.screen.messages.messageActionSheet.title",
                        ),
                    },
                    index => {
                        switch (myMessage ? index : index + 2) {
                            case 0: // Delete
                                onDelete()
                                break
                            case 1:
                                onEdit()
                                break
                            case 2: // Copy
                                onCopy()
                                break
                            case 3: // Cancel
                                break
                            default:
                                unreachable()
                        }
                    },
                ),
            "useMessageActionSheet::showMenu",
            {
                argSelector: () => [],
            },
        ),
        [onDelete, showActionSheetWithOptions],
    )

    return showMenu
}

export const useUndoMessageRemoval = (item: MessageInfoFragment) =>
    useUndoRemoveMessageMutation({
        optimisticResponse: {
            __typename: "Mutation",
            undoRemoveMessage: {...item},
        },
        update: logger.debug(
            (cache, {data, errors}) => {
                if (errors || !data?.undoRemoveMessage) {
                    return
                }

                const existing = readQuery<
                    GetMessagesQuery,
                    GetMessagesQueryVariables
                >(cache, {
                    query: GetMessagesDocument,
                    variables: {
                        conversationId: data.undoRemoveMessage.conversation.id,
                    },
                })
                const getMessages = existing?.getMessages
                    ? addToSortedArray(
                          existing.getMessages,
                          data.undoRemoveMessage,
                          compareMessagesByDate,
                      )
                    : [data.undoRemoveMessage]
                cache.writeQuery<GetMessagesQuery, GetMessagesQueryVariables>({
                    data: {
                        __typename: "Query",
                        getMessages,
                    },
                    query: GetMessagesDocument,
                    variables: {
                        conversationId: data.undoRemoveMessage.conversation.id,
                    },
                })
            },
            "UndoRemoveMessageMutation::update",
            {
                argSelector: (_cache, {data}) => [data?.undoRemoveMessage],
            },
        ),
        variables: {id: item.id},
    })

export const useRemoveMessage = (item: MessageInfoFragment) => {
    const {conversation, id} = item

    return useRemoveMessageMutation({
        optimisticResponse: {
            __typename: "Mutation",
            removeMessage: {...item},
        },
        update: logger.debug(
            (cache, {data, errors}) => {
                if (errors || !data?.removeMessage) {
                    return
                }

                const existing = readQuery<
                    GetMessagesQuery,
                    GetMessagesQueryVariables
                >(cache, {
                    query: GetMessagesDocument,
                    variables: {
                        conversationId: data.removeMessage.conversation.id,
                    },
                })
                const getMessages = (existing?.getMessages ?? []).filter(
                    message => message.id !== data.removeMessage?.id,
                )
                logger.debug`Updated getMessages from ${existing?.getMessages ??
                    []} to ${getMessages}`

                cache.writeQuery<GetMessagesQuery, GetMessagesQueryVariables>({
                    data: {
                        __typename: "Query",
                        getMessages,
                    },
                    query: GetMessagesDocument,
                    variables: {
                        conversationId: data.removeMessage.conversation.id,
                    },
                })

                /*
                TODO: Review this and the error below:

                Missing field lastMessage in {
                "__typename": "Conversation",
                "id": "2d8c115f-19d3-4354-99ec-7605961868ad"
                }
             */
                // update the last message of the conversation
                if (conversation.lastMessage?.id === id) {
                    const lastMessage: MessageInfoFragment | undefined =
                        getMessages[getMessages.length - 1]
                    cache.writeFragment<LastMessageFragment>({
                        data: {
                            __typename: "Conversation",
                            lastMessage,
                            lastMessageUpdatedAt: lastMessage?.createdAt,
                        },
                        fragment: LastMessageFragmentDoc,
                        fragmentName: "LastMessage",
                        id,
                    })
                }
            },
            "RemoveMessageMutation::update",
            {
                argSelector: (_cache, {data}) => [data?.removeMessage],
            },
        ),
        variables: {id},
    })
}

export const useRemoveMessageWithUndo = (message: MessageInfoFragment) => {
    const [remove] = useRemoveMessage(message)
    const [undo] = useUndoMessageRemoval(message)
    return [remove, undo] as const
}

export interface MessageListItemProps {
    highlight: boolean
    item: MessageInfoFragment
    onEditMessage: () => void
}
export const MessageListItem: React.FC<MessageListItemProps> = ({
    highlight,
    item,
    onEditMessage,
}) => {
    const sendSnackMessage = useSnackbarMessage()
    const {
        author: {isMe},
        content,
        createdAt,
        id,
    } = item
    const [remove, undo] = useRemoveMessageWithUndo(item)
    // TODO: Set up infrastructure for supporting "read" messages.
    const status = useMemo((): MessageStatus => {
        if (isOptimisticId(id)) {
            return {type: "sending"}
        }
        return {type: "sent"}
    }, [id])

    const onDeleteMessage = useCallback(async () => {
        if (isMe) {
            await remove()
            sendSnackMessage({
                action: {
                    label: "UNDO",
                    onPress: undo,
                },
                message: translate(
                    "ui.screen.messages.successfullyRemovedMessage",
                ),
            })
        }
    }, [isMe, remove, sendSnackMessage, undo])

    const onCopyMessage = useCallback(() => {
        switch (content.__typename) {
            case "TextContent":
                Clipboard.setString(content.content)
                sendSnackMessage(
                    translate("ui.screen.messages.clipboard.savedToClipboard"),
                )
                break
            default:
                sendSnackMessage(
                    translate(
                        "ui.screen.messages.clipboard.canOnlySaveTextMessages",
                    ),
                )
                break
        }
    }, [content, sendSnackMessage])

    const showMenu = useMessageActionSheet(
        onDeleteMessage,
        onEditMessage,
        onCopyMessage,
        item.author.isMe,
    )

    const date = useDate(createdAt)

    const defaultProps = {
        date,
        highlight,
        id,
        isMe,
        onLongPress: showMenu,
        status: isMe ? status : undefined,
    }

    switch (content.__typename) {
        case "AudioContent":
            return (
                <AudioMessageListItem
                    {...{
                        ...defaultProps,
                        audioContentUrl: content.audio.presignedUrl,
                    }}
                />
            )
        case "ImageContent":
            return (
                <ImageMessageListItem
                    {...{
                        ...defaultProps,
                        imageContentUrl: content.image.presignedUrl,
                    }}
                />
            )
        case "TextContent":
            return (
                <TextMessageListItem
                    {...{
                        ...defaultProps,
                        content: content.content,
                    }}
                />
            )
        default:
            unreachable()
    }
}

export interface InformationPromptProps {
    content: string
    icon?: string
}
export const InformationPrompt: React.FC<InformationPromptProps> = ({
    content,
    icon,
}) => {
    const [visible, setVisible] = useState(true)
    const onPress = useCallback(() => setVisible(false), [setVisible])

    return (
        <View style={styles.lookingForLawyerContainer}>
            <Banner
                visible={visible}
                actions={[{label: translate("ui.banner.close"), onPress}]}
                icon={icon}>
                {`${content} ${translate(
                    "ui.screen.messages.confidentialityNotice",
                )}`}
            </Banner>
        </View>
    )
}

export interface InformationGatheringPromptProps {}
export const InformationGatheringPrompt: React.FC<InformationGatheringPromptProps> = () => (
    <InformationPrompt
        icon="briefcase-edit"
        content={translate("ui.screen.messages.informationGatheringPrompt")}
    />
)

export interface LookingForLawyerPromptProps {}
export const LookingForLawyerPrompt: React.FC<LookingForLawyerPromptProps> = () => (
    <InformationPrompt
        icon="school"
        content={translate("ui.screen.messages.lookingForLawyerPrompt")}
    />
)

export interface InProgressPromptProps {
    conversation: ConversationInfoFragment
}
export const InProgressPrompt: React.FC<InProgressPromptProps> = ({
    conversation,
}) => {
    const content = useMemo(() => {
        const title = conversation?.lawyer?.lawyerTitle?.toLowerCase()
        if (!title) {
            return translate("ui.screen.messages.inProgressPromptNoTitle")
        }
        return translate("ui.screen.messages.inProgressPrompt", {
            lawyerTitle: title,
        })
    }, [conversation])
    return <InformationPrompt icon="school" content={content} />
}

const resolveCaseOptimisticResponse = (
    data: GetConversationQuery | undefined,
): HookOptimisticResponseSync<typeof useResolveCaseMutation> => () => {
    assert(data?.getConversation)

    return {
        __typename: "Mutation",
        resolveCase: {
            ...data.getConversation,
            status: "Resolved",
        },
    }
}

const unresolveCaseOptimisticResponse = (
    data: GetConversationQuery | undefined,
): HookOptimisticResponseSync<typeof useUnresolveCaseMutation> => () => {
    assert(data?.getConversation)

    return {
        __typename: "Mutation",
        unresolveCase: {
            ...data.getConversation,
            status: "InProgress",
        },
    }
}

const onBackPressedDefault = () => Actions.pop()
export interface MessagesHeaderProps {
    button?: {
        color?: string
        onLongPress: () => void
        onPress: () => void
        title: string
    }
    onBackPressed?: () => void
    subtitle?: string
    title: string
}
export const MessagesHeader: React.FC<MessagesHeaderProps> = ({
    button,
    onBackPressed = onBackPressedDefault,
    subtitle,
    title,
}) => (
    <Appbar.Header style={styles.appbarHeaderStyle}>
        <Appbar.BackAction onPress={onBackPressed} />
        <Appbar.Content
            subtitleStyle={styles.messagesHeaderSubtitleStyle}
            {...{subtitle, title}}
        />

        {button && (
            <TouchableOpacity
                onPress={button.onPress}
                onLongPress={button.onLongPress}>
                <Button color={button.color ?? "white"}>{button.title}</Button>
            </TouchableOpacity>
        )}
    </Appbar.Header>
)

/*
TODO: fix warnings while deleting
muteWarnings.fx.js:18 Missing field lastMessageUpdatedAt in {
  "__typename": "Conversation"
}
muteWarnings.fx.js:18 Missing field lastMessage in {
  "__typename": "Conversation"
}
*/
export interface MessagesProps {
    highlightedMessageId?: string
    id: string
    subtitle?: string
    title: string
}
export const Messages: React.FC<MessagesProps> = ({
    highlightedMessageId,
    id,
}) => {
    const [editMessage, setEditMessage] = useState<MessageInfoFragment>()
    const sendMessage = useSnackbarMessage()
    const {data: currentUserData} = useCurrentUserQuery()
    const {
        data: getConversationData,
        loading: getConversationLoading,
    } = useGetConversationQuery({variables: {id}})
    useConversationUpdateSubscription(id)
    const {data, fetchMore, loading: messagesLoading, refetch} = useMessages(id)

    const [resolve] = useResolveCaseMutation({
        optimisticResponse: resolveCaseOptimisticResponse(getConversationData),
        variables: {id},
    })
    const [unresolve] = useUnresolveCaseMutation({
        optimisticResponse: unresolveCaseOptimisticResponse(
            getConversationData,
        ),
        variables: {id},
    })
    const [
        markAsLookingForLawyer,
    ] = useMarkConversationAsLookingForLawyerMutation({variables: {id}})
    const messages = useMemo(() => data?.getMessages ?? [], [data])
    const onRefresh = useCallback(() => {
        logger.info`Message list refresh requested. Refreshing...`
        refetch()
    }, [refetch])
    const onEndReached = useCallback(async () => {
        logger.info`Reached the end of the message list. Fetching more...`

        try {
            await fetchMore({
                updateQuery: (prev, {fetchMoreResult}) => ({
                    ...prev,
                    getMessages: [
                        ...(prev?.getMessages ?? []),
                        ...(fetchMoreResult?.getMessages ?? []),
                    ],
                }),
                variables: {
                    offset: messages.length,
                },
            })
        } catch (error) {
            logger.debug`Got the following error during fetchMore: ${error}`
        }
    }, [fetchMore, messages.length])
    const onPressCheckmarkClient = useCallback(
        () =>
            runAsync(async () => {
                assert(
                    getConversationData?.getConversation?.status ===
                        "InformationGathering",
                )

                await markAsLookingForLawyer()
                sendMessage(
                    translate("ui.screen.messages.changedStatusToLFLMessage"),
                )
            }),
        [getConversationData, markAsLookingForLawyer, sendMessage],
    )
    const onPressCheckmarkLawyer = useCallback(
        () =>
            runAsync(async () => {
                assert(
                    getConversationData?.getConversation?.status ===
                        "InProgress",
                )
                await resolve()
                sendMessage(
                    translate(
                        "ui.screen.messages.successfullyResolvedCaseMessage",
                    ),
                )
            }),
        [getConversationData, resolve, sendMessage],
    )
    const onPressUnresolveLawyer = useCallback(
        () =>
            runAsync(async () => {
                assert(
                    getConversationData?.getConversation?.status === "Resolved",
                )
                await unresolve()
                sendMessage(
                    translate(
                        "ui.screen.messages.successfullyUnresolvedCaseMessage",
                    ),
                )
            }),
        [getConversationData, unresolve, sendMessage],
    )
    const disableImageAndRecording = useMemo(() => {
        const caseStatus =
            getConversationData?.getConversation?.status ?? "LookingForLawyer"
        return (
            caseStatus === "InformationGathering" ||
            caseStatus === "LookingForLawyer"
        )
    }, [getConversationData])

    const showCheckmarkClient = useMemo(
        () =>
            currentUserData?.currentUser?.user.__typename === "Client" &&
            getConversationData?.getConversation?.status ===
                "InformationGathering",
        [currentUserData, getConversationData],
    )
    const showCheckmarkLawyerInProgress = useMemo(
        () =>
            currentUserData?.currentUser?.user.__typename === "Lawyer" &&
            getConversationData?.getConversation?.status === "InProgress",
        [currentUserData, getConversationData],
    )
    const showCheckmarkLawyerResolved = useMemo(
        () =>
            currentUserData?.currentUser?.user.__typename === "Lawyer" &&
            getConversationData?.getConversation?.status === "Resolved",
        [currentUserData, getConversationData],
    )
    const button = useMemo<MessagesHeaderProps["button"]>(() => {
        if (showCheckmarkClient) {
            return {
                onLongPress: () => {
                    sendMessage(
                        translate(
                            "ui.screen.messages.checkmarkHoverMessageClient",
                        ),
                    )
                },
                onPress: onPressCheckmarkClient,
                title: translate("ui.screen.messages.headerButtons.submit"),
            }
        }

        if (showCheckmarkLawyerInProgress) {
            return {
                onLongPress: () => {
                    sendMessage(
                        translate("ui.screen.messages.checkmarkResolveHover"),
                    )
                },
                onPress: onPressCheckmarkLawyer,
                title: translate("ui.screen.messages.headerButtons.resolve"),
            }
        }

        if (showCheckmarkLawyerResolved) {
            return {
                onLongPress: () => {
                    sendMessage(
                        translate("ui.screen.messages.checkmarkUnresolveHover"),
                    )
                },
                onPress: onPressUnresolveLawyer,
                title: translate("ui.screen.messages.headerButtons.unresolve"),
            }
        }

        return undefined
    }, [
        onPressCheckmarkClient,
        onPressCheckmarkLawyer,
        onPressUnresolveLawyer,
        sendMessage,
        showCheckmarkClient,
        showCheckmarkLawyerInProgress,
        showCheckmarkLawyerResolved,
    ])

    return (
        <View style={styles.container}>
            <MessagesHeader
                button={button}
                subtitle={
                    getConversationData?.getConversation
                        ? getStatusText(
                              getConversationData.getConversation,
                              true,
                          )
                        : undefined
                }
                title={getConversationData?.getConversation?.case.title ?? ""}
            />

            {getConversationData?.getConversation?.status ===
                "InformationGathering" && <InformationGatheringPrompt />}
            {getConversationData?.getConversation?.status ===
                "LookingForLawyer" && <LookingForLawyerPrompt />}
            {getConversationData?.getConversation?.status === "InProgress" &&
                currentUserData?.currentUser?.user.__typename === "Client" && (
                    <InProgressPrompt
                        conversation={getConversationData.getConversation}
                    />
                )}

            <ConversationImageContextProvider data={data}>
                <InvertedFlatList
                    refreshing={getConversationLoading || messagesLoading}
                    onRefresh={onRefresh}
                    style={styles.messageList}
                    data={messages}
                    extraData={editMessage}
                    keyExtractor={message => message.id}
                    renderItem={({item}) => (
                        <MessageListItem
                            onEditMessage={() => setEditMessage(item)}
                            item={item}
                            highlight={
                                item.id === highlightedMessageId ||
                                item.id === editMessage?.id
                            }
                        />
                    )}
                    onEndReachedThreshold={0.35}
                    onEndReached={onEndReached}
                />
            </ConversationImageContextProvider>

            {editMessage ? (
                <MessageEditInputBar
                    conversationId={id}
                    {...{editMessage, setEditMessage}}
                />
            ) : (
                <MessagesInputBar
                    conversationId={id}
                    sendSnackMessage={sendMessage}
                    disableImageButton={disableImageAndRecording}
                    disableRecordingButton={disableImageAndRecording}
                />
            )}
            <KeyboardSpacer />
        </View>
    )
}
